if (!customElements.get('ci-course-preview')) {
  customElements.define('ci-course-preview', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.video = this.querySelector('video');
      this.button = this.querySelector('button');
      this.generation = 0;
      this.playing = false;
      this.pending = false;
      this.visible = false;
      this.manualPaused = false;
      this.blocked = false;
      this.retries = 0;
      this.button.addEventListener('click', () => {
        if (this.pending || !this.video.paused) {
          this.manualPaused = true;
          this.pause();
        } else {
          this.manualPaused = false;
          this.blocked = false;
          this.retries = 0;
          this.play();
        }
      }, {signal});
      this.video.addEventListener('playing', () => {
        if (this.manualPaused || document.hidden) { this.pause(); return; }
        if (this.video.paused) return;
        this.playing = true;
        const generation = this.generation;
        const reveal = () => {
          if (generation === this.generation && this.playing && !this.video.paused && this.video.readyState >= 2) this.setAttribute('data-frame-ready', '');
        };
        // A decoded frame, not a resolved play() promise, releases the poster.
        if (this.video.requestVideoFrameCallback) this.video.requestVideoFrameCallback(reveal);
        else requestAnimationFrame(() => requestAnimationFrame(reveal));
        this.update();
      }, {signal});
      for (const event of ['waiting', 'emptied', 'error', 'pause']) {
        this.video.addEventListener(event, () => {
          this.playing = false;
          this.removeAttribute('data-frame-ready');
          this.update();
        }, {signal});
      }
      this.video.addEventListener('canplay', () => this.sync(), {signal});
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pause();
        else this.sync();
      }, {signal});
      // Independent cards keep both MP4s out of the initial page load.
      this.loadObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting) && !document.hidden) this.prepare();
      }, {rootMargin: '150px 0px', threshold: 0});
      this.observer = new IntersectionObserver(entries => {
        const entry = entries[entries.length - 1];
        const visible = entry.isIntersecting && entry.intersectionRatio >= .05;
        if (visible !== this.visible) {
          this.visible = visible;
          this.retries = 0;
        }
        this.sync();
      }, {threshold: [0, .05]});
      this.loadObserver.observe(this);
      this.observer.observe(this);
      this.update();
    }
    prepare() {
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.volume = 0;
      this.video.playsInline = true;
      this.video.loop = true;
      if (!this.video.hasAttribute('src')) {
        this.video.preload = 'metadata';
        this.video.src = this.video.dataset.source;
      }
    }
    sync() {
      if (document.hidden || !this.visible || this.manualPaused) { this.pause(); return; }
      if (!this.blocked && !this.pending && this.video.paused) this.play();
    }
    play() {
      if (this.pending || document.hidden) return;
      this.prepare();
      this.pending = true;
      const generation = ++this.generation;
      let result;
      // Synchronous invocation preserves Safari's manual-click activation.
      try { result = this.video.play(); } catch (error) { result = Promise.reject(error); }
      Promise.resolve(result).then(() => {
        if (generation === this.generation) this.pending = false;
      }).catch(error => {
        if (generation !== this.generation) return;
        this.pending = false;
        this.playing = false;
        this.removeAttribute('data-frame-ready');
        this.update();
        if (error.name === 'AbortError' && this.retries < 2) {
          this.retries++;
          this.timer = setTimeout(() => {
            if (generation === this.generation) this.sync();
          }, 150);
        } else {
          // No retry loop for policy denial. Manual Play remains available.
          this.blocked = true;
        }
      });
    }
    pause() {
      this.generation++;
      clearTimeout(this.timer);
      this.pending = false;
      this.video.pause();
      this.playing = false;
      this.removeAttribute('data-frame-ready');
      this.update();
    }
    update() {
      const active = this.playing && !this.video.paused;
      const label = active ? this.dataset.pauseLabel : this.dataset.playLabel;
      this.button.querySelector('[data-label]').textContent = label;
      this.button.querySelector('[data-icon]').textContent = active ? '\u2161' : '\u25b7';
      this.button.setAttribute('aria-label', `${label} \u2014 ${this.video.getAttribute('aria-label')}`);
    }
    disconnectedCallback() {
      this.pause();
      this.observer.disconnect();
      this.loadObserver.disconnect();
      this.abort.abort();
      this.abort = null;
    }
  });
}
